import React, { RefObject, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import Modal from "components/modal/Modal";
import { Button } from "components/v2/button/Button";
import { selectPreferredLanguage } from "features/authSlice";
import { getPreferredLanguage, isSpanishLanguage } from "utilities/getPreferredLanguage";
import "./post-los-lock-modal.scss";

export interface PostLosLockModalProps {
	readonly onClose: () => void;
	readonly returnFocusRef?: RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function PostLosLockModal({ onClose, returnFocusRef }: PostLosLockModalProps) {
	const modalRef = useRef<HTMLDialogElement>(null);
	const preferredLanguage = useSelector(selectPreferredLanguage);
	const isSpanishUi = useMemo(
		() => isSpanishLanguage(getPreferredLanguage(preferredLanguage)),
		[preferredLanguage]
	);

	const closeModal = () => {
		onClose();
		window.setTimeout(() => returnFocusRef?.current?.focus(), 0);
	};

	useEffect(() => {
		document.body.classList.add("preferred-language-modal-open");
		return () => document.body.classList.remove("preferred-language-modal-open");
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!modalRef.current) {
				return;
			}

			if (event.key === "Escape") {
				closeModal();
				return;
			}

			if (event.key !== "Tab") {
				return;
			}

			const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

			if (!focusableElements.length) {
				return;
			}

			const firstFocusableElement = focusableElements[0];
			const lastFocusableElement = focusableElements[focusableElements.length - 1];

			if (event.shiftKey && document.activeElement === firstFocusableElement) {
				event.preventDefault();
				lastFocusableElement.focus();
			} else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
				event.preventDefault();
				firstFocusableElement.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, []);

	useEffect(() => {
		const firstFocusableElement = modalRef.current?.querySelector<HTMLElement>("button");
		firstFocusableElement?.focus();
	}, []);

	const customHeader = (
		<div className="post-los-lock-modal__header">
			<h3 id="post-los-lock-modal-title" className="post-los-lock-modal__title">
				Language Set
			</h3>
			<button
				type="button"
				className="post-los-lock-modal__close-btn"
				aria-label="Close language set modal"
				onClick={closeModal}
			>
				<FontAwesomeIcon icon={regular("xmark")} />
			</button>
		</div>
	);

	const customBody = (
		<div className="post-los-lock-modal__body">
			<div className={`post-los-lock-modal__segment${isSpanishUi ? " GlobalLinkTx" : ""}`}>
				<p id="post-los-lock-modal-description" className="post-los-lock-modal__description">
					Your language preference has been set.
				</p>
			</div>
			<div className={`post-los-lock-modal__segment${isSpanishUi ? " GlobalLinkTx" : ""}`}>
				<p className="post-los-lock-modal__description post-los-lock-modal__description--single-line">
					If you'd like to change it, please contact your Loan Officer.
				</p>
			</div>
		</div>
	);

	const footer = (
		<div className="post-los-lock-modal__footer">
			<Button
				label="Close"
				className="post-los-lock-modal__close-cta"
				onClick={closeModal}
			/>
		</div>
	);

	return ReactDOM.createPortal(
		<dialog
			open
			className={`post-los-lock-modal${isSpanishUi ? " post-los-lock-modal--spanish" : ""}`}
			ref={modalRef}
			aria-labelledby="post-los-lock-modal-title"
			aria-describedby="post-los-lock-modal-description"
		>
			<Modal customHeader={customHeader} customBody={customBody} footer={footer} footerContentPosition="right" />
		</dialog>,
		document.body,
	);
}
